/* eslint-disable no-param-reassign */
/* eslint-disable react/forbid-prop-types */
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveBar } from '@nivo/bar';

import CustomLegend from '../../utils/chartTools/createLegend';
import customTooltip from '../../utils/chartTools/handleCustomTooltip';
import colorSelector from '../../utils/formatCharts/handleColor';
import customAxis, { customPercentAxisRight } from '../../utils/formatCharts/formatAxis';
import { dataSort, genParetoData, dataFiller } from '../../utils/formatCharts/handleData';
import { getBiggestTick } from '../../utils/formatCharts/getAxisTicks';
import { labelValuesStr } from '../../utils/formatCharts/handleCustomValue';
import ExtraLine from '../../utils/chartTools/ExtraLine';
import KpiStatus from '../../utils/chartTools/KpiStatus';

const emptyObj = {};

const Pareto = ({
  data, styleConfig, isEdit, globalConfig = emptyObj,
}) => {
  const [chartData, setChartData] = useState({ ready: false });
  const [filteredChartData, setFilteredChartData] = useState({ data: [], keys: [] });
  const [hiddenIds, setHiddenIds] = useState([]);

  useEffect(() => {
    setChartData(genParetoData(dataFiller(data, styleConfig)));
  }, [
    data,
    styleConfig.DataFormat,
    styleConfig.ExtendDateLine,
  ]);

  useEffect(() => {
    if (chartData.ready) {
      setChartData((cData) => ({
        ...cData,
        data: dataSort(chartData.data, styleConfig),
      }));
    }
  }, [
    ...styleConfig.CustomSortControl.kpiCategories,
    styleConfig.SortValuesControl,
  ]);

  const biggestTick = useMemo(() => (
    getBiggestTick(chartData?.total ?? 0, styleConfig)
  ), [chartData, styleConfig.GroupModeControl]);

  const dataFrom = () => {
    if (styleConfig?.ColorByControl === 'keys') return 'id';
    if (styleConfig?.ColorByControl === 'indexes') return 'indexValue';
    if (chartData.keys.length > 1) return 'id';
    return 'indexValue';
  };

  const createLabel = (label) => {
    if (
      styleConfig.EnableLabelMinControl
      && (label.value * 100) / chartData.total < (styleConfig.LabelMinPercentControl ?? 0)
    ) return null;
    const val = labelValuesStr(label.value, null, styleConfig);
    if (styleConfig.LabelFormatControl === 'Central') return val;
    return <tspan y={-10}>{val}</tspan>;
  };

  const createTooltip = (val) => customTooltip(val, 'pareto', styleConfig);

  // eslint-disable-next-line react/prop-types
  const ExtraInfo = ({ bars, yScale }) => (
    <ExtraLine
      bars={bars}
      yScale={yScale}
      dataKey="lineAbsolute"
      lineType="Pareto"
      tooltip={createTooltip}
      styleConfig={styleConfig}
      globalConfig={globalConfig}
    />
  );

  useEffect(() => {
    if (chartData.ready) {
      setFilteredChartData(dataFrom() === 'indexValue' ? {
        data: chartData.data.filter((item) => !hiddenIds.includes(item.uid)),
        keys: chartData.keys,
      } : {
        data: chartData.data,
        keys: chartData.keys.filter((k) => !hiddenIds.includes(k)),
      });
    }
  }, [chartData, hiddenIds]);

  return chartData.ready ? (
    <KpiStatus
      conditions={([
        [
          'NoLineSelected',
          data?.index?.[0] === 'Totais',
        ],
      ])}
    >
      <ResponsiveBar
        data={filteredChartData.data}
        keys={filteredChartData.keys}
        indexBy="label"
        // valueScale={{ type: styleConfig.ValueScaleControl || 'linear' }}
        /* GENERAL */
        margin={styleConfig?.Margin || {
          top: 50,
          right: 145,
          bottom: 55,
          left: 60,
        }}
        valueScale={{
          type: styleConfig.ValueScaleControl || 'linear',
          reverse: !!styleConfig?.ReverseControl?.checked || false,
          min: styleConfig?.MinValueControl?.checked ? 'auto' : (
            styleConfig?.MinValueControl?.value
          ),
          max: styleConfig.MaxValueControl?.checked ? biggestTick : (
            styleConfig.MaxValueControl?.value
          ),
          nice: true,
        }}
        groupMode={styleConfig.GroupModeControl || 'stacked'}
        layout={styleConfig.LayoutControl || 'vertical'}
        // reverse={!!styleConfig?.ReverseControl?.checked || false}
        // minValue={styleConfig?.MinValueControl?.checked
        //   ? 'auto' : styleConfig?.MinValueControl?.value}
        // maxValue={styleConfig.MaxValueControl?.checked
        //   ? biggestTick : styleConfig.MaxValueControl?.value}
        padding={styleConfig?.PaddingControl?.value ?? 0.1}
        innerPadding={styleConfig?.InnerPaddingControl?.value ?? 0}
        /* BORDER */
        borderRadius={styleConfig?.BorderRadiusControl?.value ?? 5}
        borderWidth={styleConfig?.BorderWidthControl?.value ?? 0}
        borderColor={
          styleConfig.BorderColorControl
            ? styleConfig.BorderColorControl
            : { from: 'color', modifiers: [['darker', 1.6]] }
        }
        /* LABEL */
        enableLabel={styleConfig.EnableLabelControl ? styleConfig.EnableLabelControl.checked : true}
        labelSkipWidth={styleConfig.labelSkipWidth || 0}
        labelSkipHeight={styleConfig.labelSkipHeight || 0}
        labelTextColor={styleConfig.LabelTextColorControl}
        label={createLabel}
        axisTop={customAxis('top', styleConfig, globalConfig, styleConfig.LayoutControl === 'horizontal')}
        axisLeft={customAxis('left', styleConfig, globalConfig, styleConfig.LayoutControl !== 'horizontal')}
        axisBottom={customAxis('bottom', styleConfig, globalConfig, styleConfig.LayoutControl === 'horizontal')}
        axisRight={customPercentAxisRight(styleConfig, globalConfig, chartData.total ?? 1)}
        /* TOOLTIP */
        isInteractive={styleConfig.InteractiveControl}
        tooltip={createTooltip}
        /* LEGEND */
        enableGridX={styleConfig?.EnableGridX ?? false}
        enableGridY={styleConfig?.EnableGridY ?? false}
        layers={[
          'grid',
          'axes',
          'bars',
          ExtraInfo,
          'markers',
          styleConfig?.LegendControl?.checked ? (dt) => (
            <CustomLegend
              chartDataAr={dataFrom() === 'id' ? chartData.keys : chartData.data}
              colorParts={dt.bars.reduce((aux, p) => {
                aux[p.data[dataFrom()]] = p.color;
                return aux;
              }, {})}
              sizes={{
                width: dt.innerWidth,
                height: dt.innerHeight,
              }}
              config={styleConfig}
              setToggleIds={setHiddenIds}
              isEdit={isEdit}
            />
          ) : null,
          'annotations',
        ]}
        /* STYLES/THEMES */
        colorBy={dataFrom()}
        colors={dataFrom() === 'id' ? colorSelector({
          dataLength: chartData.keys.length, config: styleConfig, colorKey: 'id', globalConfig,
        }) : colorSelector({
          dataLength: chartData.data.length, config: styleConfig, colorKey: 'indexValue', globalConfig,
        })}
        theme={{
          legends: {
            text: {
              fontSize: `${styleConfig?.LegendsFontSizeControl?.value}px`,
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLegend : styleConfig?.LegendsFontFamilyControl?.value}', Arial`,
              fill: styleConfig?.LegendFontColor ?? '#999',
              fontWeight: styleConfig?.LegendBold ? 'bold' : 'normal',
              fontStyle: styleConfig?.LegendItalic ? 'italic' : 'normal',
            },
            hidden: {
              text: {
                textDecoration: 'line-through',
              },
            },
          },
          labels: {
            text: {
              fontSize: `${styleConfig?.LabelsFontSizeControl ? styleConfig?.LabelsFontSizeControl.value : 13}px`,
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLabel : styleConfig?.LabelsFontFamilyControl?.value}', Arial`,
              transform: `translate(${styleConfig?.LabelTranslateX ? styleConfig?.LabelTranslateX : 0}px, ${styleConfig?.LabelTranslateY ? styleConfig?.LabelTranslateY : 0}px) rotate(${styleConfig?.LabelRotationControl ? styleConfig?.LabelRotationControl : 0}deg)`,
              transformOrigin: 'center center',
              transformBox: 'fill-box',
            },
          },
          axis: {
            legend: {
              text: {
                fontSize: styleConfig?.AxisGlobal?.legendFontSize,
                fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontAxis : styleConfig?.AxisGlobal?.fontFamily}', Arial`,
                fill: styleConfig?.AxisGlobal?.color,
              },
            },
            ticks: {
              text: {
                fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontAxis : styleConfig?.AxisGlobal?.fontFamily}', Arial`,
                fontSize: styleConfig?.AxisGlobal?.fontSize,
                fill: styleConfig?.AxisGlobal?.color,
              },
            },
          },
        }}
        /* ETC */
        animate={styleConfig.InteractiveControl ? !!styleConfig.Animate : false}
        motionConfig={styleConfig.InteractiveControl ? (styleConfig.MotionConfig || 'default') : 'default'}
      />
    </KpiStatus>
  ) : <></>;
};

Pareto.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  styleConfig: PropTypes.objectOf(PropTypes.any).isRequired,
  isEdit: PropTypes.bool.isRequired,
  globalConfig: PropTypes.objectOf(PropTypes.any),
};

export default Pareto;
